// NOTE (Ryan, 2026-07-30): Client-safe content contract shared by the
// SDK content runtime (server), the Content miniapp editor (browser), the
// coordinator (route scaffolding), and the website builder (preview
// detection). It owns the file-format contract — slugs, layout names, the
// collection schema, field-value validity, and public visibility — so those
// consumers cannot drift. `@replohq/sdk/content` is `server-only`, which is
// why this lives in schemas instead of the SDK. Keep this module
// dependency-light (zod only); the YAML frontmatter half of the contract
// lives in `contentFrontmatter.ts`.

import { z } from "zod";

/**
 * The shared app-root manifest that bundles `content/**` for production
 * builds (published Workers have no filesystem). Scaffolded as a placeholder
 * by the coordinator, imported by the generated templates, and populated by
 * the harness publish tool before `next build` — one constant so those three
 * can never drift.
 */
export const CONTENT_MANIFEST_FILENAME = "content-manifest.generated.json";

/** Site-relative directory that holds all content. */
export const CONTENT_DIRNAME = "content";

/** Per-collection schema file inside `content/<collection>/`. */
export const CONTENT_COLLECTION_SCHEMA_FILENAME = "_collection.json";

/** Extension of entry files inside `content/<collection>/`. */
export const CONTENT_ENTRY_FILE_EXTENSION = ".mdx";

/**
 * Frontmatter key that stores the `relatedContent` field's value (an array of
 * `<collection>/<entry>` paths). The storage key predates the field id; both
 * the editor's serializer and the SDK renderer map between them through this
 * constant.
 */
export const CONTENT_RELATED_VALUE_STORAGE_KEY = "related";

/**
 * One slug contract for collection and entry slugs: they become directory
 * names, file names, and public URL segments in every consumer. The pattern
 * is ASCII-only, so the length cap counts bytes and characters identically.
 */
export const CONTENT_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/u;
export const CONTENT_MAX_SLUG_LENGTH = 100;

export const contentSlugSchema = z
  .string()
  .min(1)
  .max(CONTENT_MAX_SLUG_LENGTH)
  .regex(CONTENT_SLUG_PATTERN);

export function isValidContentSlug(slug: string): boolean {
  return contentSlugSchema.safeParse(slug).success;
}

export const CONTENT_ENTRY_STATUSES = [
  "draft",
  "published",
  "archived",
] as const;

export type ContentEntryStatus = (typeof CONTENT_ENTRY_STATUSES)[number];

export const contentEntryStatusSchema = z.enum(CONTENT_ENTRY_STATUSES);

/**
 * The one public-visibility rule: only published, non-archived entries render
 * on the published site. The sandbox preview (`next dev`) and the website
 * builder pass `allowDrafts: true` so drafts are previewable before publish.
 */
export function isContentEntryPubliclyVisible({
  status,
  archivedAt,
  allowDrafts,
}: {
  status: ContentEntryStatus;
  archivedAt: unknown;
  allowDrafts: boolean;
}): boolean {
  if (archivedAt !== null && archivedAt !== undefined) {
    return false;
  }
  return status === "published" || (allowDrafts && status === "draft");
}

export const contentCollectionFieldTypeSchema = z.enum([
  "short-text",
  "long-text",
  "rich-text",
  "image",
  "tags",
  "related",
  "date",
  "url",
  "status",
  "boolean",
]);

export type ContentCollectionFieldType = z.infer<
  typeof contentCollectionFieldTypeSchema
>;

export const contentCollectionColorSchema = z.enum([
  "seashell",
  "yellow",
  "lightGreen",
  "darkGreen",
  "lightPink",
  "magenta",
  "purple",
  "blue",
]);

export const contentCollectionFieldSchema = z.object({
  id: z.string().min(1).max(128),
  label: z.string().min(1).max(200),
  type: contentCollectionFieldTypeSchema,
  required: z.boolean(),
  enabled: z.boolean(),
  helpText: z.string().optional(),
  builtin: z.boolean(),
});

export type ContentCollectionField = z.infer<
  typeof contentCollectionFieldSchema
>;

/**
 * The `_collection.json` file contract. `passthrough` keeps unknown keys so
 * editor round-trips never drop data another writer added.
 */
export const contentCollectionFileSchema = z
  .object({
    name: z.string().min(1).max(200),
    description: z.string(),
    icon: z.string().min(1),
    color: contentCollectionColorSchema,
    tags: z.array(z.string()),
    fields: z.array(contentCollectionFieldSchema).max(200),
    archivedAt: z.iso.datetime().nullish(),
    hasListingPage: z.boolean().optional(),
  })
  .passthrough();

/**
 * Whether a URL is renderable on a published page. Relative URLs resolve
 * against a dummy base so path-only values pass; only http(s) may be an
 * image source, while links additionally allow mailto/tel.
 */
export function isSafeContentUrl(
  value: string,
  { image }: { image: boolean },
): boolean {
  try {
    const url = new URL(value, "https://content.local");
    return image
      ? url.protocol === "http:" || url.protocol === "https:"
      : ["http:", "https:", "mailto:", "tel:"].includes(url.protocol);
  } catch {
    return false;
  }
}

/**
 * Validity of a present (non-empty) value for a field type. This decides both
 * editor-side validation errors and whether the SDK renders the entry at all,
 * so the two can never disagree about what a valid value is.
 */
export function getContentFieldValueSchema(
  fieldType: ContentCollectionFieldType,
): z.ZodType {
  if (
    fieldType === "short-text" ||
    fieldType === "long-text" ||
    fieldType === "rich-text"
  ) {
    return z.string();
  }
  if (fieldType === "image") {
    return z
      .string()
      .refine((value) => isSafeContentUrl(value, { image: true }));
  }
  if (fieldType === "tags" || fieldType === "related") {
    return z.array(z.string());
  }
  if (fieldType === "date") {
    return z.string().refine((value) => !Number.isNaN(Date.parse(value)));
  }
  if (fieldType === "url") {
    return z
      .string()
      .refine((value) => isSafeContentUrl(value, { image: false }));
  }
  if (fieldType === "status") {
    return contentEntryStatusSchema;
  }
  return z.boolean();
}

/**
 * Slugs a collection may never claim even when the segment doesn't exist yet:
 * conventional Next.js route namespaces every site is expected to grow into.
 * Segments the site already owns are rejected separately at scaffold time.
 */
// `new` is the create-form route segment in the Content miniapp, so an entry
// or collection with that slug would be unreachable behind it.
export const CONTENT_RESERVED_COLLECTION_SLUGS = ["api", "new"] as const;
export const CONTENT_RESERVED_ENTRY_SLUGS = ["new"] as const;
const reservedEntrySlugSet: ReadonlySet<string> = new Set(
  CONTENT_RESERVED_ENTRY_SLUGS,
);

export function isContentReservedEntrySlug(slug: string): boolean {
  return reservedEntrySlugSet.has(slug);
}

/**
 * - `content`: rendered on the published page and ordered by collection
 *   settings (all custom fields are content).
 * - `publishing`: publish controls (never rendered as page content).
 * - `seo`: head metadata, not body content.
 * - `private`: never leaves the editor.
 */
export type ContentFieldRole = "content" | "publishing" | "seo" | "private";

/**
 * Role of every builtin collection field. `satisfies` makes adding a builtin
 * without classifying it a compile error wherever field ids are typed as
 * `ContentBuiltinFieldId`.
 */
export const CONTENT_BUILTIN_FIELD_ROLES = {
  title: "content",
  author: "content",
  excerpt: "content",
  coverImage: "content",
  body: "content",
  tags: "content",
  relatedContent: "content",
  slug: "publishing",
  status: "publishing",
  publishedAt: "publishing",
} as const satisfies Record<string, ContentFieldRole>;

export type ContentBuiltinFieldId = keyof typeof CONTENT_BUILTIN_FIELD_ROLES;

/** Entry-level frontmatter keys that are head metadata, not collection fields. */
export const CONTENT_SEO_VALUE_KEYS = ["seoTitle", "seoDescription"] as const;

/** Entry-level frontmatter keys that must never be publicly rendered. */
export const CONTENT_PRIVATE_VALUE_KEYS = ["scratchpad", "backlinks"] as const;

/**
 * Values that render and edit with a parent content field instead of owning
 * an ordered slot of their own.
 */
export const CONTENT_COUPLED_VALUE_PARENTS = {
  coverImageAlt: "coverImage",
  authorInitials: "author",
} as const satisfies Record<string, ContentBuiltinFieldId>;

const privateValueKeySet: ReadonlySet<string> = new Set(
  CONTENT_PRIVATE_VALUE_KEYS,
);
const seoValueKeySet: ReadonlySet<string> = new Set(CONTENT_SEO_VALUE_KEYS);
const reservedCollectionSlugSet: ReadonlySet<string> = new Set(
  CONTENT_RESERVED_COLLECTION_SLUGS,
);

/**
 * A related-content reference as stored in `related` frontmatter:
 * `<collectionSlug>/<entrySlug>`. Producer and consumer share this pair so
 * the written form and the published renderer can't drift apart.
 */
export function formatContentEntryRef({
  collectionSlug,
  entrySlug,
}: {
  collectionSlug: string;
  entrySlug: string;
}): string {
  return `${collectionSlug}/${entrySlug}`;
}

/** Null when the ref isn't a `<collectionSlug>/<entrySlug>` pair. */
export function parseContentEntryRef(
  ref: string,
): { collectionSlug: string; entrySlug: string } | null {
  // Tolerate the on-disk file form an agent might hand-write.
  const normalized = ref.replace(/^content\//u, "").replace(/\.mdx$/u, "");
  const segments = normalized.split("/");
  if (segments.length !== 2) {
    return null;
  }
  const [collectionSlug, entrySlug] = segments;
  if (!collectionSlug || !entrySlug) {
    return null;
  }
  return { collectionSlug, entrySlug };
}

export function isContentPrivateValueKey(key: string): boolean {
  return privateValueKeySet.has(key);
}

export function isContentSeoValueKey(key: string): boolean {
  return seoValueKeySet.has(key);
}

export function isContentReservedCollectionSlug(slug: string): boolean {
  return reservedCollectionSlugSet.has(slug);
}

/** Custom (non-builtin) collection fields are always content. */
export function getContentFieldRole(fieldId: string): ContentFieldRole {
  if (isContentSeoValueKey(fieldId)) {
    return "seo";
  }
  if (isContentPrivateValueKey(fieldId)) {
    return "private";
  }
  const builtinRoles: Record<string, ContentFieldRole> =
    CONTENT_BUILTIN_FIELD_ROLES;
  return builtinRoles[fieldId] ?? "content";
}

/**
 * Enabled fields in collection-settings order, for the published page. The
 * "Visible" toggle is authoritative here — including lifecycle fields like
 * status and the slug, which the entry editor renders as dedicated controls
 * rather than ordered fields. Private keys never publish.
 */
export function getPublishedContentFields<
  Field extends { id: string; enabled: boolean },
>(fields: Field[]): Field[] {
  return fields.filter((field) => {
    return field.enabled && getContentFieldRole(field.id) !== "private";
  });
}

/**
 * Enabled content-role fields in collection-settings order — what the entry
 * editor renders as the ordered field list.
 */
export function getOrderedContentFields<
  Field extends { id: string; enabled: boolean },
>(fields: Field[]): Field[] {
  return fields.filter((field) => {
    return field.enabled && getContentFieldRole(field.id) === "content";
  });
}
